import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Center } from '@/components/ui';
import { Loader } from '@/components/ui/feedback';
import MainLayout from '../../../layouts/MainLayout';
import ProtectedRoute from '@/components/ccms-components/auth/components/ProtectedRoute';
import SocialMediaDashboard from './dashboard';

/**
 * Social Media Analytics routes.
 *
 * The landing page is imported eagerly since it is what most visits want; everything
 * else is lazy behind one Suspense, matching TaskManagement.
 *
 * Every path the sidebar offers resolves to a real route here. Previously four of the
 * five menu entries fell through to the catch-all and silently bounced back to the
 * dashboard, which reads as a broken link.
 */
const PlatformPage = lazy(() => import('./platform'));
const ContentPage = lazy(() => import('./content'));
const VideoPage = lazy(() => import('./video'));
const AudiencePage = lazy(() => import('./audience'));
const AdsPage = lazy(() => import('./ads'));
const ReportsPage = lazy(() => import('./reports'));
const AdminAccountsPage = lazy(() => import('./admin/accounts'));
const AdminThresholdsPage = lazy(() => import('./admin/thresholds'));

function LoadingFallback() {
  return (
    <Center h={400}>
      <Loader size="lg" />
    </Center>
  );
}

export default function SocialMediaManagementRoutes() {
  return (
    <MainLayout domainKey="social-media">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<SocialMediaDashboard />} />

          {/* One component serves all three platforms; the slug scopes it. */}
          <Route path="/platform/:platform" element={<PlatformPage />} />

          <Route path="/content" element={<ContentPage />} />
          <Route path="/video" element={<VideoPage />} />
          <Route path="/audience" element={<AudiencePage />} />
          <Route path="/ads" element={<AdsPage />} />
          <Route path="/reports/*" element={<ReportsPage />} />

          {/* Managing accounts and alert limits is an administrative action. */}
          <Route
            path="/admin/accounts"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <AdminAccountsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/thresholds"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <AdminThresholdsPage />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/app/social-media" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
